// drawer קונסנט מכל נתיב (11 §1: ?consent=<id>; ?consent=all — רשימת הממתינים).
// תוכן הפריטים נשלף בזהות הצופה (08), מדופדף; סימון נשמר בין עמודים; "אשר הכול" / "אשר את המסומנים" / "דחה הכול".
// שלושה מצבי קצה שאומרים את האמת (v1 consent-host): אין הרשאה / לא נמצא — מה-api; תקלה בהחלטה — "לא אושר ולא נדחה".
import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { aiApi } from '../lib/aiApi.ts';
import type { ConsentDetail, ScopeRequestView } from '../lib/aiContract.ts';
import { ApiError } from '../lib/api.ts';
import { T } from '../lib/strings.ts';
import { fmtWhen } from '../lib/time.ts';
import { StatusLabel } from './ui/badge.tsx';
import { ErrorView, Loading } from './Status.tsx';
import { Button } from './ui/button.tsx';
import { Sheet } from './ui/sheet.tsx';

const PAGE = 10;

export function useOpenConsent() {
  const [sp, setSp] = useSearchParams();
  return useCallback(
    (id: string) => {
      const n = new URLSearchParams(sp);
      n.set('consent', id);
      setSp(n);
    },
    [sp, setSp],
  );
}

export function consentHref(search: string, id: string) {
  const n = new URLSearchParams(search);
  n.set('consent', id);
  return `?${n}`;
}

export function ConsentDrawer() {
  const [sp, setSp] = useSearchParams();
  const id = sp.get('consent');
  const close = useCallback(() => {
    const n = new URLSearchParams(sp);
    n.delete('consent');
    setSp(n);
  }, [sp, setSp]);
  return (
    <Sheet open={!!id} onClose={close} title={id === 'all' ? T.consent.listTitle : T.consent.title} testId="consent-drawer">
      <div className="flex items-center gap-2 border-b border-border p-3">
        <h2 className="m-0 me-auto text-lg font-bold">{id === 'all' ? T.consent.listTitle : T.consent.title}</h2>
        <Button variant="ghost" size="sm" onClick={close} data-testid="consent-close">
          {T.consent.close}
        </Button>
      </div>
      <div className="flex flex-col gap-3 p-3">{id === 'all' ? <ConsentList /> : id ? <ConsentView key={id} id={id} /> : null}</div>
    </Sheet>
  );
}

function ConsentList() {
  const q = useQuery({ queryKey: ['consents'], queryFn: aiApi.listConsents, staleTime: 0 });
  const [sp] = useSearchParams();
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data.consents.length) return <p className="m-0 text-muted-foreground">{T.consent.none}</p>;
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="consent-list">
      {q.data.consents.map((k) => (
        <li key={k.id}>
          <Link to={consentHref(sp.toString(), k.id)} className="flex min-h-11 flex-col rounded-md border border-border bg-card p-3 hover:border-primary/50">
            <span className="font-medium">{k.titleHe}</span>
            <span className="text-sm text-muted-foreground">
              {T.consent.items(k.itemsTotal)} · <bdi dir="ltr">{k.interfaceId}.{k.action}</bdi>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Preview({ value }: { value: unknown }) {
  if (value == null) return null;
  return (
    <details className="text-sm">
      <summary className="cursor-pointer text-muted-foreground">{T.consent.preview}</summary>
      <pre dir="ltr" className="m-0 mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-start text-xs whitespace-pre-wrap">
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}

/** 14/20: מה ההיקף המבוקש — אתרים (web), נמענים (mail.send) או התיבה של המשתמש (mail.read). */
function ScopeWhat({ scope }: { scope: ScopeRequestView['scope'] }) {
  if (scope.recipients !== undefined)
    return (
      <>
        <dt className="text-muted-foreground">{T.consent.scopeRecipients}</dt>
        <dd className="m-0 font-medium" data-testid="consent-scope-recipients">
          {scope.recipients === '*' ? T.consent.scopeAllRecipients : <bdi dir="ltr">{scope.recipients.join(', ')}</bdi>}
        </dd>
      </>
    );
  if (scope.mailbox)
    return (
      <>
        <dt className="text-muted-foreground">{T.consent.scopeTitle}</dt>
        <dd className="m-0 font-medium" data-testid="consent-scope-mailbox">
          {T.consent.scopeMailbox}
          {scope.since ? (
            <>
              {' '}
              · {T.consent.scopeSince} <bdi dir="auto">{fmtWhen(scope.since)}</bdi>
            </>
          ) : null}
        </dd>
      </>
    );
  return (
    <>
      <dt className="text-muted-foreground">{T.consent.scopeHosts}</dt>
      <dd className="m-0 font-medium" data-testid="consent-scope-hosts">
        {scope.hosts === '*' ? T.consent.scopeAll : <bdi dir="ltr">{(scope.hosts ?? []).map((h) => (h === 'web-search' ? T.consent.scopeSearch : h)).join(', ')}</bdi>}
      </dd>
    </>
  );
}

type MailPreviewValue = { to: string[]; cc?: string[]; subject: string; textHead?: string; attachments?: Array<{ name: string; bytes: number }> };
function isMailPreview(v: unknown): v is MailPreviewValue {
  const o = v as Partial<MailPreviewValue> | null;
  return !!o && Array.isArray(o.to) && typeof o.subject === 'string';
}
const fmtBytes = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toLocaleString('he-IL', { maximumFractionDigits: 1 })}MB` : `${Math.max(1, Math.round(n / 1024)).toLocaleString('he-IL')}KB`);

/** 20 §1.3: פריט מייל — מה שיוצא בדיוק: נמענים, נושא, תחילת הטקסט, קבצים מצורפים עם גודל. */
function MailPreview({ p }: { p: MailPreviewValue }) {
  return (
    <dl className="m-0 mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md bg-muted/50 p-2 text-sm" data-testid="mail-preview">
      <dt className="text-muted-foreground">{T.consent.mailTo}</dt>
      <dd className="m-0 min-w-0 break-words" data-testid="mail-preview-to">
        <bdi dir="ltr">{p.to.join(', ')}</bdi>
      </dd>
      {p.cc && p.cc.length > 0 && (
        <>
          <dt className="text-muted-foreground">{T.consent.mailCc}</dt>
          <dd className="m-0 min-w-0 break-words" data-testid="mail-preview-cc">
            <bdi dir="ltr">{p.cc.join(', ')}</bdi>
          </dd>
        </>
      )}
      <dt className="text-muted-foreground">{T.consent.mailSubject}</dt>
      <dd className="m-0 font-medium" data-testid="mail-preview-subject">
        <bdi dir="auto">{p.subject}</bdi>
      </dd>
      {p.textHead && (
        <>
          <dt className="text-muted-foreground">{T.consent.mailText}</dt>
          <dd className="m-0 whitespace-pre-wrap" data-testid="mail-preview-text">
            <bdi dir="auto">{p.textHead}</bdi>
          </dd>
        </>
      )}
      {p.attachments && p.attachments.length > 0 && (
        <>
          <dt className="text-muted-foreground">{T.consent.mailFiles}</dt>
          <dd className="m-0">
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0" data-testid="mail-preview-files">
              {p.attachments.map((f, i) => (
                <li key={`${f.name}-${i}`}>
                  <bdi dir="auto">{f.name}</bdi> <span className="text-muted-foreground">({fmtBytes(f.bytes)})</span>
                </li>
              ))}
            </ul>
          </dd>
        </>
      )}
    </dl>
  );
}

function ConsentView({ id }: { id: string }) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(0);
  const [sel, setSel] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [fault, setFault] = useState<string | null>(null);
  const [result, setResult] = useState<{ approved: number; executed: number } | null>(null);
  const q = useQuery({ queryKey: ['consent', id, from], queryFn: () => aiApi.getConsent(id, from, PAGE), staleTime: 0, placeholderData: (p) => p });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} />;
  const d: ConsentDetail = q.data;
  const k = d.consent;
  const total = k.itemsTotal;
  const pending = k.status === 'pending';
  const toggle = (itemId: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(itemId)) n.delete(itemId);
      else n.add(itemId);
      return n;
    });
  const pageIds = d.items.map((i) => i.id);
  const allPage = pageIds.length > 0 && pageIds.every((i) => sel.has(i));

  const decide = async (approve: 'all' | string[]) => {
    setBusy(true);
    setFault(null);
    try {
      const r = await aiApi.decideConsent(id, approve);
      setResult({ approved: r.approved, executed: r.executed });
      setSel(new Set());
      await qc.invalidateQueries({ queryKey: ['consent', id] });
      void qc.invalidateQueries({ queryKey: ['consents'] });
      void qc.invalidateQueries({ queryKey: ['panel'] });
    } catch (e) {
      // 403/404/409 — מה שהשרת אמר; כל השאר — "לא אושר ולא נדחה"
      setFault(e instanceof ApiError && e.status >= 400 && e.status < 500 ? e.textHe : T.consent.fault);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="consent-view" data-consent-id={k.id}>
      <header className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="m-0 me-auto text-lg font-semibold">{k.titleHe}</h3>
          <StatusLabel
            data-testid="consent-status"
            data-status={k.status}
            tone={k.status === 'pending' ? 'warn' : k.status === 'rejected' || k.status === 'expired' ? 'error' : 'ok'}
          >
            {T.ai.consentStatus[k.status] ?? k.status}
          </StatusLabel>
        </div>
        <p className="m-0 text-sm text-muted-foreground">
          <bdi dir="ltr">
            {k.interfaceId}.{k.action}
          </bdi>{' '}
          · {T.consent.items(total)} · {T.consent.requestedBy}: {k.requestedBy.nameHe} · {T.consent.expires} <bdi dir="auto">{fmtWhen(k.expiresAt)}</bdi>
        </p>
      </header>

      {k.scopeRequest && (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="consent-scope">
          <ScopeWhat scope={k.scopeRequest.scope} />
          <dt className="text-muted-foreground">{T.consent.scopeUntil}</dt>
          <dd className="m-0" data-testid="consent-scope-until">
            {k.scopeRequest.until ? <bdi dir="auto">{fmtWhen(k.scopeRequest.until)}</bdi> : T.consent.scopeNoLimit}
          </dd>
          {k.scopeRequest.purposeHe && (
            <>
              <dt className="text-muted-foreground">{T.consent.scopePurpose}</dt>
              <dd className="m-0" data-testid="consent-scope-purpose">
                {k.scopeRequest.purposeHe}
              </dd>
            </>
          )}
        </dl>
      )}

      {result && (
        <p role="status" className="m-0 rounded-md bg-ok/10 p-2 text-sm" data-testid="consent-result">
          {T.consent.result(result.approved, result.executed)}
        </p>
      )}
      {fault && (
        <p role="alert" className="m-0 rounded-md border border-error/40 bg-error/5 p-2 text-sm" data-testid="consent-fault">
          {fault}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span data-testid="consent-range" className="me-auto">
          {T.consent.range(Math.min(total, from + 1), Math.min(total, from + d.items.length), total)}
        </span>
        {pending && (
          <span data-testid="consent-selected" data-count={sel.size}>
            {T.consent.selected(sel.size)}
          </span>
        )}
      </div>

      {pending && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setSel((s) => new Set([...s, ...pageIds]))} disabled={allPage}>
            {T.consent.selectPage}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSel(new Set())} disabled={!sel.size}>
            {T.consent.clear}
          </Button>
        </div>
      )}

      <ol className="m-0 flex list-none flex-col gap-2 p-0" data-testid="consent-items" aria-busy={q.isFetching}>
        {d.items.map((it) => (
          <li key={it.id} className="rounded-md border border-border bg-card p-2" data-testid="consent-item" data-item-id={it.id} data-status={it.status}>
            <label className="flex min-h-11 cursor-pointer items-center gap-3">
              {pending && <input type="checkbox" className="size-5 shrink-0" checked={sel.has(it.id)} onChange={() => toggle(it.id)} aria-label={it.summaryHe} />}
              <bdi dir="ltr" className="w-8 shrink-0 text-sm text-muted-foreground tabular-nums">
                {it.seq}
              </bdi>
              <span className="me-auto">{it.summaryHe}</span>
              {!pending && <span className="text-sm text-muted-foreground">{T.consent.itemStatus[it.status] ?? it.status}</span>}
            </label>
            {k.interfaceId === 'mail.send' && isMailPreview(it.preview) ? <MailPreview p={it.preview} /> : <Preview value={it.preview} />}
          </li>
        ))}
      </ol>

      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" data-testid="consent-prev" disabled={from === 0 || q.isFetching} onClick={() => setFrom(Math.max(0, from - PAGE))}>
          <span aria-hidden="true">→</span> {T.consent.prev}
        </Button>
        <Button variant="outline" size="sm" data-testid="consent-next" disabled={from + PAGE >= total || q.isFetching} onClick={() => setFrom(from + PAGE)}>
          {T.consent.next} <span aria-hidden="true">←</span>
        </Button>
      </div>

      {pending && (
        <div className="sticky bottom-0 z-10 flex flex-wrap gap-2 border-t border-border bg-background pt-3">
          <Button data-testid="consent-approve-selected" disabled={busy || !sel.size} onClick={() => void decide([...sel])}>
            {busy ? T.consent.busy : T.consent.approveSelected(sel.size)}
          </Button>
          <Button variant="outline" data-testid="consent-approve-all" disabled={busy} onClick={() => void decide('all')}>
            {T.consent.approveAll}
          </Button>
          <Button variant="destructive" data-testid="consent-reject" disabled={busy} onClick={() => void decide([])}>
            {T.consent.rejectAll}
          </Button>
        </div>
      )}
    </div>
  );
}
